import React, { useMemo } from "react";
import { Box, Typography, Grid, useTheme } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { format } from "date-fns";
import { formatNumber } from "../../../lib/formatNumber";
import NewColors from "../../../shared/theme/NewColors";

function ChartFrame({ title, subtitle, children, emptyMessage }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.paper",
        minWidth: 0,
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>
        {title}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {subtitle}
        </Typography>
      ) : null}
      {emptyMessage ? (
        <Box
          sx={{
            height: 260,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`,
            bgcolor: NewColors.sectionBackground,
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
            {emptyMessage}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </Box>
      )}
    </Box>
  );
}

/**
 * Prominent charts for the Workouts → History tab (same derived metrics as coach training charts).
 */
function WorkoutsChartsPanel({ metrics, weightUnit = "lbs" }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const volumeRows = useMemo(() => {
    const series = metrics?.weeklyVolumeSeries ?? [];
    return series.map((w) => ({
      label: format(w.weekStart instanceof Date ? w.weekStart : new Date(w.weekStart), "MMM d"),
      volume: Math.round(w.volume ?? 0),
    }));
  }, [metrics?.weeklyVolumeSeries]);

  const countRows = useMemo(() => {
    const perWeek = metrics?.workoutsPerWeek?.perWeek ?? [];
    return perWeek.map((w) => ({
      label: format(w.weekStart instanceof Date ? w.weekStart : new Date(w.weekStart), "MMM d"),
      count: w.count ?? 0,
    }));
  }, [metrics?.workoutsPerWeek?.perWeek]);

  const volEmpty = volumeRows.length === 0;
  const countEmpty = countRows.length === 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} lg={6}>
        <ChartFrame
          title="Training volume by week"
          subtitle={`Total weight × reps (${weightUnit}), summed per week · last 90 days`}
          emptyMessage={
            volEmpty
              ? "Complete a few workouts to see how your weekly training volume trends."
              : null
          }
        >
          {!volEmpty && (
            <BarChart data={volumeRows} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${primary}18`} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: NewColors.text.secondary }} axisLine={false} tickLine={false} />
              <YAxis
                tick={{ fontSize: 11, fill: NewColors.text.secondary }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => formatNumber(v, 0)}
              />
              <Tooltip
                formatter={(value) => [`${formatNumber(value, 0)} ${weightUnit}`, "Volume"]}
                labelFormatter={(label) => `Week of ${label}`}
                contentStyle={{ borderRadius: 8 }}
              />
              <Bar dataKey="volume" fill={primary} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          )}
        </ChartFrame>
      </Grid>
      <Grid item xs={12} lg={6}>
        <ChartFrame
          title="Workouts per week"
          subtitle="Number of completed sessions per week · last 90 days"
          emptyMessage={
            countEmpty ? "Your completed workouts will appear here week by week." : null
          }
        >
          {!countEmpty && (
            <BarChart data={countRows} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={`${primary}18`} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: NewColors.text.secondary }} axisLine={false} tickLine={false} />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 11, fill: NewColors.text.secondary }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value) => [value, "Workouts"]}
                labelFormatter={(label) => `Week of ${label}`}
                contentStyle={{ borderRadius: 8 }}
              />
              <Bar dataKey="count" fill={theme.palette.secondary.main} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          )}
        </ChartFrame>
      </Grid>
    </Grid>
  );
}

export default WorkoutsChartsPanel;
